// AI 批改結果(correctingResult)的解析防呆(kanban 1000412)。
//
// AI 偶發回傳壞 JSON(實例:物件成員之間混入孤立的 `e` 字元),過去前端兩種收法都錯:
//   - 老師檢視紀錄(ClassFeedback):JSON.parse 失敗 → filter 回 false,整筆無聲消失
//   - 學生首頁(StudentHome):解析失敗 → score '0',學生誤以為考了 0 分
// 這裡把「解析 + 正常/parseError 歸類」抽成純邏輯:失敗筆保留並標 parseError,
// 畫面以「批改失敗」顯示。不碰 DOM 也不打 API — 方便 vitest(node)直測。

import { isBatchUploadedRecord } from "./classRecordDelete";

/** 去掉 AI 回傳外層的 ```json fence(與既有兩頁的清理寫法一字不差) */
export const cleanCorrectingResult = (raw: string): string =>
  raw.replace(/```json\n|\n```/g, "");

export type CorrectingResultParse =
  | { ok: true; value: unknown }
  | { ok: false };

/**
 * 解析 correctingResult:
 * - 已是物件(後端原樣/先前已就地轉換過)→ 直接成功,原引用回傳
 * - 字串 → 去 fence 後 JSON.parse;丟例外即失敗
 * - 其他(null/undefined/數字…)→ 失敗
 * 字串的成敗判準只看 JSON.parse 是否丟例外,與 ClassFeedback 既有行為一致
 * (解析成功筆的後續行為不得改變)。
 */
export const parseCorrectingResult = (raw: unknown): CorrectingResultParse => {
  if (raw !== null && typeof raw === "object") return { ok: true, value: raw };
  if (typeof raw !== "string") return { ok: false };
  try {
    return { ok: true, value: JSON.parse(cleanCorrectingResult(raw)) };
  } catch {
    return { ok: false };
  }
};

/**
 * 取總分;取不到回 undefined,由呼叫端決定顯示 —— 不再回 '0'。
 * 「總分就是 0」(離題卷)是合法分數,回 "0" 不回 undefined。
 *
 * 兩種格式都認(kanban 1000517):舊格式 `整體評分.總分`、新格式頂層 `totalScore`。
 * 既有紀錄永遠是舊格式(不做資料遷移),所以這不是過渡期措施。
 */
export const extractTotalScore = (parsed: unknown): string | undefined => {
  const obj = parsed as
    | { 整體評分?: { 總分?: unknown }; totalScore?: unknown }
    | null
    | undefined;
  const total = obj?.整體評分?.總分 ?? obj?.totalScore;
  return total === undefined || total === null ? undefined : String(total);
};

/** 只綁用得到的欄位,不綁完整 Record 型別以便單測 */
export interface ParseGuardRecordLike {
  correctingResult: unknown;
  parseError?: boolean;
}

export interface SplitRecordsResult<T> {
  /** 解析成功筆:字串已(就地)換成物件,行為與改前的 filter 寫法完全相同 */
  parsed: T[];
  /** 解析失敗筆:parseError=true,correctingResult 保持原始字串不動(供追查) */
  broken: T[];
}

/**
 * 把紀錄清單分成 正常/parseError 兩類(kanban 1000412)。
 *
 * 沿用 ClassFeedback 既有的就地轉換:成功解析的字串直接 mutate
 * record.correctingResult 為物件(改前 filter callback 就是這樣寫,下游全靠
 * 這個行為);失敗筆 mutate parseError 旗標、原始字串原樣保留。
 * 非字串非物件(undefined 等舊資料)照既有行為放行到 parsed —— 這種筆過去
 * 就以 '-' 分數正常顯示,不屬於本次要標的壞 JSON。
 * 最佳/第一次的分組計算只吃 parsed,parseError 筆自然被排除。
 */
/**
 * 壞 JSON 紀錄給不給「重新批改」鈕(kanban 1000412)。
 * 只有批次來源(studentID `batch_` 開頭)的 classrecord,_id 才同時是
 * batch-results 的 batchResultID,能打 PUT /batch-results/{id}/status requeue;
 * 學生／老師自行上傳的紀錄沒有對應 batchResult,打了會 404 或誤改別筆,
 * 一律不給(維持只有「刪除」)。缺 _id 的筆打不了 API,同樣不給。
 */
export const canRegradeBrokenRecord = (
  record: { studentID?: unknown; _id?: unknown } | null | undefined
): boolean =>
  isBatchUploadedRecord(record?.studentID) &&
  typeof record?._id === "string" &&
  record._id !== "";

export function splitRecordsByParseError<T extends ParseGuardRecordLike>(
  records: T[]
): SplitRecordsResult<T> {
  const parsed: T[] = [];
  const broken: T[] = [];
  for (const record of records) {
    if (typeof record.correctingResult !== "string") {
      parsed.push(record);
      continue;
    }
    const result = parseCorrectingResult(record.correctingResult);
    if (result.ok) {
      record.correctingResult = result.value;
      parsed.push(record);
    } else {
      record.parseError = true;
      broken.push(record);
    }
  }
  return { parsed, broken };
}
